import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Building2,
  Calendar,
  MessageSquare,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Scale,
  ClipboardCheck,
  FolderOpen,
  Landmark,
  ShieldCheck,
} from 'lucide-react';
import { usePersona, Persona } from '@/contexts/PersonaContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUnreadMessageCount } from '@/hooks/useUnreadMessageCount';

interface NavItem {
  title: string;
  href: string;
  icon: React.ElementType;
  roles: Persona[];
}

const dashboardRoutes: Record<Persona, string> = {
  lawyer: '/lawyer-dashboard',
  agent: '/agent-dashboard',
  client: '/client-dashboard',
  superadmin: '/superadmin-dashboard',
};

const navItems: NavItem[] = [
  { title: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Dashboard', href: '/superadmin-dashboard', icon: ShieldCheck, roles: ['superadmin'] },
  { title: 'Transactions', href: '/transactions', icon: FileText, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Properties', href: '/properties', icon: Building2, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Messages', href: '/messages', icon: MessageSquare, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Documents', href: '/documents', icon: FolderOpen, roles: ['lawyer', 'client'] },
  { title: 'Payments', href: '/payments', icon: Landmark, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Calendar', href: '/calendar', icon: Calendar, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Matter Workspace', href: '/transactions', icon: Scale, roles: ['lawyer'] },
  { title: 'Due Diligence Queue', href: '/due-diligence', icon: ClipboardCheck, roles: ['lawyer'] },
];

const bottomItems: NavItem[] = [
  { title: 'Settings', href: '/settings', icon: Settings, roles: ['lawyer', 'agent', 'client', 'superadmin'] },
  { title: 'Help Center', href: '/help', icon: HelpCircle, roles: ['lawyer', 'agent', 'client', 'superadmin'] },
];

export function AppSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { persona } = usePersona();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { data: unreadMessageCount = 0 } = useUnreadMessageCount();

  useEffect(() => {
    if (isMobile) {
      setCollapsed(false);
    }
  }, [isMobile]);

  // Map dashboard href based on persona
  const filteredNavItems = navItems
    .filter((item) => item.roles.includes(persona))
    .map((item) => 
      item.title === 'Dashboard' 
        ? { ...item, href: dashboardRoutes[persona] }
        : item
    );
  const filteredBottomItems = bottomItems.filter((item) => item.roles.includes(persona));

  const NavItemComponent = ({ item }: { item: NavItem }) => {
    // Check if active - for dashboard, also check persona-specific route
    const isDashboard = item.title === 'Dashboard';
    const isActive = isDashboard 
      ? (location.pathname === item.href || location.pathname === '/' && item.href === dashboardRoutes[persona])
      : location.pathname === item.href;
    const Icon = item.icon;
    const showMessagesBadge = item.title === 'Messages' && unreadMessageCount > 0;

    const content = (
      <NavLink
        to={item.href}
        className={cn(
          'relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group',
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
        )}
      >
        <Icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'text-sidebar-primary')} />
        {!collapsed && (
          <span className="text-sm font-medium truncate animate-fade-in">
            {item.title}
          </span>
        )}
        {showMessagesBadge && !collapsed && (
          <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 py-0.5 text-[11px] font-semibold text-destructive-foreground">
            {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
          </span>
        )}
        {showMessagesBadge && collapsed && (
          <span className="absolute right-2 top-2 inline-flex min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-4 text-destructive-foreground">
            {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
          </span>
        )}
        {isActive && !collapsed && !showMessagesBadge && (
          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-sidebar-primary" />
        )}
      </NavLink>
    );

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.title}
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <aside
      role="navigation"
      aria-label="Main navigation"
      className={cn(
        'sticky top-16 h-[calc(100svh-4rem)] self-start bg-sidebar border-r border-sidebar-border flex flex-col overflow-hidden transition-all duration-300 ease-in-out',
        collapsed ? 'w-[72px]' : 'w-64'
      )}
    >
      {/* Toggle Button */}
      <div className="p-3 flex justify-end">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="w-8 h-8 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </Button>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 space-y-1 overflow-hidden">
        {filteredNavItems.map((item) => (
          <NavItemComponent key={item.href} item={item} />
        ))}
      </nav>

      {/* Bottom Navigation */}
      <div className="px-3 pb-4 space-y-1 border-t border-sidebar-border pt-4 mt-2">
        {filteredBottomItems.map((item) => (
          <NavItemComponent key={item.href} item={item} />
        ))}
      </div>
    </aside>
  );
}
